// Per-shot image quality, from one 256px RGBA read of the chosen frame.
// Pure (no DOM) so node tests run it. Every result is a HINT: nothing here
// blocks the shutter or Confirm.

const CELL = 8;
const D_BG = 60;
// Foreground = colour far from both background centres. A gradient-energy OR
// (spec) welded the cup to the table edge on the real fixtures, so it is dropped.
const D_FG = 80;
const CLIP_Y = 245;
const CLIP_CHROMA = 30;
const CELL_GLARE_FRAC = 0.3;
const GLARE_MIN_BLOB = 6;
const GLARE_PEAK = 20;
const GLARE_BAD_PERMILLE = 4;
const GLARE_BAD_BLOB = 15;
const MAX_BLOBS = 64;
const GLARE_PRINT = 18; // mean gradient energy around a blob that means printed area
const RING_FLAT = 4; // ring gradient energy above this is print, not a backdrop (backdrops < 2.5, labels > 5.5)
const TEXT_E = 8; // gradient energy a background cell needs to count as textured
const TEXT_FRAC = 0.25; // share of textured background cells that means 'label, not backdrop'
const TOO_FAR = [0.3, 0.4]; // longest bbox side / frame side: first shot, later shots
const EDGE_PX = 3; // product within this many px of the frame edge counts as touching it

/**
 * Below this a frame is blurry. PROVISIONAL: measured on one Android handset,
 * where sharp frames scored 100x the blurred ones. ?debug=1 shows each shot's
 * raw score. A hint only.
 */
export const SOFT_BELOW = 45;

/** Variance of the Laplacian on a greyscale buffer. Same loop the camera always scored with. */
export function laplacianVariance(grey, w, h) {
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap =
        4 * grey[i] - grey[i - 1] - grey[i + 1] - grey[i - w] - grey[i + w];
      sum += lap;
      sumSq += lap * lap;
      n++;
    }
  }
  if (!n) return 0;
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

export function createAnalyzer() {
  let np = 0;
  let nc = 0;
  let grey, clip, lab, stack;
  let cR, cG, cB, cY, cE, cC, cP, fg, tmp, clab, cstack, med;
  const blobs = [];
  const hist = new Uint32Array(256);

  function alloc(w, h, cells) {
    if (w * h !== np) {
      np = w * h;
      grey = new Float32Array(np);
      clip = new Uint8Array(np);
      lab = new Int32Array(np);
      stack = new Int32Array(np);
    }
    if (cells !== nc) {
      nc = cells;
      cR = new Float32Array(nc);
      cG = new Float32Array(nc);
      cB = new Float32Array(nc);
      cY = new Float32Array(nc);
      cE = new Float32Array(nc);
      cC = new Float32Array(nc);
      cP = new Float32Array(nc);
      fg = new Uint8Array(nc);
      tmp = new Uint8Array(nc);
      clab = new Int32Array(nc);
      cstack = new Int32Array(nc);
      med = new Float32Array(nc);
    }
  }

  function median(count) {
    if (!count) return 0;
    const v = med.subarray(0, count);
    v.sort();
    return v[count >> 1];
  }

  function analyze(rgba, w, h) {
    const cw = (w + CELL - 1) >> 3;
    const ch = (h + CELL - 1) >> 3;
    const cells = cw * ch;
    alloc(w, h, cells);
    cR.fill(0); cG.fill(0); cB.fill(0); cY.fill(0); cE.fill(0); cC.fill(0); cP.fill(0);
    lab.fill(0);

    // One pass: grey, clipped mask, per-cell colour.
    let clipped = 0;
    for (let y = 0; y < h; y++) {
      const row = (y >> 3) * cw;
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const p = i * 4;
        const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
        const Y = 0.299 * r + 0.587 * g + 0.114 * b;
        grey[i] = Y;
        const ci = row + (x >> 3);
        cR[ci] += r; cG[ci] += g; cB[ci] += b; cY[ci] += Y; cP[ci]++;
        const mx = r > g ? (r > b ? r : b) : g > b ? g : b;
        const mn = r < g ? (r < b ? r : b) : g < b ? g : b;
        if (Y >= CLIP_Y && mx - mn <= CLIP_CHROMA) {
          clip[i] = 1;
          cC[ci]++;
          clipped++;
        } else clip[i] = 0;
      }
    }
    for (let y = 1; y < h - 1; y++) {
      const row = (y >> 3) * cw;
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const gx = grey[i + 1] - grey[i - 1];
        const gy = grey[i + w] - grey[i - w];
        cE[row + (x >> 3)] += ((gx < 0 ? -gx : gx) + (gy < 0 ? -gy : gy)) * 0.5;
      }
    }
    for (let c = 0; c < cells; c++) {
      const n = cP[c];
      cR[c] /= n; cG[c] /= n; cB[c] /= n; cY[c] /= n; cE[c] /= n;
      cC[c] = cC[c] / n; // fraction clipped from here on
    }

    const sharp = laplacianVariance(grey, w, h);

    // ---- framing -----------------------------------------------------------
    let conf = false;
    let f = -1;
    let xmask = 0;
    let ext = 0;
    let fgMask = false;
    let bx0 = 0, by0 = 0, bx1 = 0, by1 = 0; // bbox in px, inclusive-exclusive
    if (cw >= 4 && ch >= 4) {
      const isRing = (cx, cy) => cx === 0 || cy === 0 || cx === cw - 1 || cy === ch - 1;
      // k-means k=2 over valid ring cells, seeded darkest and brightest.
      let lo = -1, hi = -1;
      for (let cy = 0; cy < ch; cy++) {
        for (let cx = 0; cx < cw; cx++) {
          if (!isRing(cx, cy)) continue;
          const c = cy * cw + cx;
          if (cC[c] > CELL_GLARE_FRAC) continue;
          if (lo < 0 || cY[c] < cY[lo]) lo = c;
          if (hi < 0 || cY[c] > cY[hi]) hi = c;
        }
      }
      if (lo >= 0) {
        let a0 = cR[lo], a1 = cG[lo], a2 = cB[lo];
        let b0 = cR[hi], b1 = cG[hi], b2 = cB[hi];
        let na = 0, nb = 0;
        for (let it = 0; it < 5; it++) {
          na = 0; nb = 0;
          let sa0 = 0, sa1 = 0, sa2 = 0, sb0 = 0, sb1 = 0, sb2 = 0;
          for (let cy = 0; cy < ch; cy++) {
            for (let cx = 0; cx < cw; cx++) {
              if (!isRing(cx, cy)) continue;
              const c = cy * cw + cx;
              if (cC[c] > CELL_GLARE_FRAC) continue;
              const da = (cR[c] - a0) ** 2 + (cG[c] - a1) ** 2 + (cB[c] - a2) ** 2;
              const db = (cR[c] - b0) ** 2 + (cG[c] - b1) ** 2 + (cB[c] - b2) ** 2;
              if (da <= db) { na++; sa0 += cR[c]; sa1 += cG[c]; sa2 += cB[c]; }
              else { nb++; sb0 += cR[c]; sb1 += cG[c]; sb2 += cB[c]; }
            }
          }
          if (na) { a0 = sa0 / na; a1 = sa1 / na; a2 = sa2 / na; }
          if (nb) { b0 = sb0 / nb; b1 = sb1 / nb; b2 = sb2 / nb; }
        }
        // A cluster under 25% of the ring is a product touching the edge, not background.
        const useA = na >= 0.25 * (na + nb);
        const useB = nb >= 0.25 * (na + nb);
        const distBg = (c) =>
          Math.sqrt(Math.min(
            useA ? (cR[c] - a0) ** 2 + (cG[c] - a1) ** 2 + (cB[c] - a2) ** 2 : Infinity,
            useB ? (cR[c] - b0) ** 2 + (cG[c] - b1) ** 2 + (cB[c] - b2) ** 2 : Infinity,
          ));
        // A side is background when 70% of its cells sit on a background colour.
        const side = (cells4) => {
          let ok = 0, total = 0;
          for (const c of cells4) {
            total++;
            if (cC[c] <= CELL_GLARE_FRAC && distBg(c) <= D_BG) ok++;
          }
          return ok >= 0.7 * total;
        };
        let sides = 0;
        const top = [], bottom = [], left = [], right = [];
        for (let cx = 0; cx < cw; cx++) { top.push(cx); bottom.push((ch - 1) * cw + cx); }
        for (let cy = 0; cy < ch; cy++) { left.push(cy * cw); right.push(cy * cw + cw - 1); }
        for (const s of [top, right, bottom, left]) if (side(s)) sides++;

        if (sides >= 3) {
          for (let c = 0; c < cells; c++) {
            fg[c] = cC[c] <= CELL_GLARE_FRAC && distBg(c) > D_FG ? 1 : 0;
          }
          // close by one cell: dilate then erode (3x3; outside the grid counts as set when eroding)
          for (let cy = 0; cy < ch; cy++) {
            for (let cx = 0; cx < cw; cx++) {
              let any = 0;
              for (let dy = -1; dy <= 1 && !any; dy++) {
                const yy = cy + dy;
                if (yy < 0 || yy >= ch) continue;
                for (let dx = -1; dx <= 1; dx++) {
                  const xx = cx + dx;
                  if (xx >= 0 && xx < cw && fg[yy * cw + xx]) { any = 1; break; }
                }
              }
              tmp[cy * cw + cx] = any;
            }
          }
          for (let cy = 0; cy < ch; cy++) {
            for (let cx = 0; cx < cw; cx++) {
              let all = 1;
              for (let dy = -1; dy <= 1 && all; dy++) {
                const yy = cy + dy;
                if (yy < 0 || yy >= ch) continue;
                for (let dx = -1; dx <= 1; dx++) {
                  const xx = cx + dx;
                  if (xx >= 0 && xx < cw && !tmp[yy * cw + xx]) { all = 0; break; }
                }
              }
              fg[cy * cw + cx] = all;
            }
          }
          // A textured "background" is label, not a backdrop: back-of-pack close-ups.
          let mc = 0;
          for (let cy = 0; cy < ch; cy++) {
            for (let cx = 0; cx < cw; cx++) {
              if (isRing(cx, cy) && cC[cy * cw + cx] <= CELL_GLARE_FRAC) med[mc++] = cE[cy * cw + cx];
            }
          }
          const rmed = median(mc);
          const edgeThr = Math.max(TEXT_E, 2.5 * rmed);
          let inner = 0, textured = 0;
          for (let cy = 0; cy < ch; cy++) {
            for (let cx = 0; cx < cw; cx++) {
              const c = cy * cw + cx;
              if (cC[c] > CELL_GLARE_FRAC) continue;
              if (isRing(cx, cy) || fg[c]) continue;
              inner++;
              if (cE[c] > edgeThr) textured++;
            }
          }
          const texturedBg = rmed > RING_FLAT || (inner > 0 && textured > TEXT_FRAC * inner);
          // components, best = most cells near the centre
          clab.fill(0);
          let best = 0, second = 0;
          let bl = 0;
          let bn = 0, bbx0 = 0, bby0 = 0, bbx1 = 0, bby1 = 0;
          let next = 0;
          for (let s = 0; s < cells; s++) {
            if (!fg[s] || clab[s]) continue;
            next++;
            let sp = 0, n = 0, sx = 0, sy = 0, x0 = cw, y0 = ch, x1 = -1, y1 = -1, core = 0;
            cstack[sp++] = s;
            clab[s] = next;
            while (sp) {
              const c = cstack[--sp];
              const cx = c % cw, cy = (c / cw) | 0;
              n++; sx += cx; sy += cy;
              if (cx > 0 && cy > 0 && cx < cw - 1 && cy < ch - 1 && fg[c - 1] && fg[c + 1] && fg[c - cw] && fg[c + cw]) core++;
              if (cx < x0) x0 = cx; if (cx > x1) x1 = cx;
              if (cy < y0) y0 = cy; if (cy > y1) y1 = cy;
              if (cx > 0 && fg[c - 1] && !clab[c - 1]) { clab[c - 1] = next; cstack[sp++] = c - 1; }
              if (cx < cw - 1 && fg[c + 1] && !clab[c + 1]) { clab[c + 1] = next; cstack[sp++] = c + 1; }
              if (cy > 0 && fg[c - cw] && !clab[c - cw]) { clab[c - cw] = next; cstack[sp++] = c - cw; }
              if (cy < ch - 1 && fg[c + cw] && !clab[c + cw]) { clab[c + cw] = next; cstack[sp++] = c + cw; }
            }
            // specks and thin streaks (table edges, glare trails) are not candidates
            if (n < 4 || (!core && n < 0.58 * (x1 - x0 + 1) * (y1 - y0 + 1))) continue;
            if (n / ((x1 - x0 + 1) * (y1 - y0 + 1)) < 0.35) continue;
            const dx = ((sx / n + 0.5) / cw - 0.5) * 2;
            const dy = ((sy / n + 0.5) / ch - 0.5) * 2;
            const score = n * Math.exp(-(dx * dx + dy * dy) / (2 * 0.35 * 0.35));
            if (score > best) {
              second = best; best = score; bl = next;
              bn = n; bbx0 = x0; bby0 = y0; bbx1 = x1; bby1 = y1;
            } else if (score > second) second = score;
          }
          const bbCells = (bbx1 - bbx0 + 1) * (bby1 - bby0 + 1);
          if (bn >= 4 && bn / bbCells >= 0.35 && best >= 2 * second && !texturedBg) {
            conf = true;
            f = bbCells / cells;
            bx0 = bbx0 * CELL; by0 = bby0 * CELL;
            bx1 = Math.min(w, (bbx1 + 1) * CELL); by1 = Math.min(h, (bby1 + 1) * CELL);
            ext = Math.max((bx1 - bx0) / w, (by1 - by0) / h);
            // touching an edge: a product pixel within EDGE_PX of the real frame edge, inside the component's own edge cells
            const prodPx = (x, y) => {
              const i = y * w + x, p4 = i * 4;
              const cc = (y >> 3) * cw + (x >> 3);
              if (clab[cc] !== bl) return false;
              const r = rgba[p4], g = rgba[p4 + 1], b = rgba[p4 + 2];
              const d = Math.sqrt(Math.min(
                useA ? (r - a0) ** 2 + (g - a1) ** 2 + (b - a2) ** 2 : Infinity,
                useB ? (r - b0) ** 2 + (g - b1) ** 2 + (b - b2) ** 2 : Infinity,
              ));
              return d > D_FG;
            };
            const touch = (xs, xe, ys, ye) => {
              for (let y = ys; y < ye; y++) for (let x = xs; x < xe; x++) if (prodPx(x, y)) return true;
              return false;
            };
            if (bby0 === 0 && touch(bx0, bx1, 0, Math.min(h, EDGE_PX))) xmask |= 1;
            if (bbx1 === cw - 1 && touch(Math.max(0, w - EDGE_PX), w, by0, by1)) xmask |= 2;
            if (bby1 === ch - 1 && touch(bx0, bx1, Math.max(0, h - EDGE_PX), h)) xmask |= 4;
            if (bbx0 === 0 && touch(0, Math.min(w, EDGE_PX), by0, by1)) xmask |= 8;
            fgMask = true;
          }
        }
      }
    }

    // ---- exposure, central 60% ----------------------------------------------
    const ex0 = Math.floor(w * 0.2), ex1 = Math.ceil(w * 0.8);
    const ey0 = Math.floor(h * 0.2), ey1 = Math.ceil(h * 0.8);
    hist.fill(0);
    let ySum = 0, nB = 0, nE = 0;
    for (let y = ey0; y < ey1; y++) {
      for (let x = ex0; x < ex1; x++) {
        const Y = grey[y * w + x];
        ySum += Y;
        nE++;
        hist[Y | 0]++;
        if (Y >= 250) nB++;
      }
    }
    const yMean = nE ? ySum / nE : 0;
    const k = nE ? (100 * nB) / nE : 0;
    let p50 = 255, p95 = 255, acc = 0, got50 = false;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (!got50 && acc >= 0.5 * nE) { p50 = v; got50 = true; }
      if (acc >= 0.95 * nE) { p95 = v; break; }
    }

    // a white backdrop is exposure done right: only count bright when the ring is not white
    let ringWhite = 0, ringN = 0;
    for (let cy = 0; cy < ch; cy++) {
      for (let cx = 0; cx < cw; cx++) {
        if (cx && cy && cx < cw - 1 && cy < ch - 1) continue;
        ringN++;
        if (cY[cy * cw + cx] >= 240) ringWhite++;
      }
    }
    const whiteBg = ringN > 0 && ringWhite >= 0.5 * ringN;
    // ---- glare ---------------------------------------------------------------
    // Only with a confident product box: on hand-held shots the unconfident test flagged half the good
    // photos. No box, no glare reading (g = 0).
    blobs.length = 0;
    for (let s = 0; s < np && conf && !whiteBg; s++) {
      if (!clip[s] || lab[s]) continue;
      let sp = 0, area = 0, sY = 0, sx = 0, sy = 0, x0 = w, y0 = h, x1 = -1, y1 = -1;
      stack[sp++] = s;
      lab[s] = 1;
      while (sp) {
        const i = stack[--sp];
        const x = i % w, y = (i / w) | 0;
        area++; sY += grey[i]; sx += x; sy += y;
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (x > 0 && clip[i - 1] && !lab[i - 1]) { lab[i - 1] = 1; stack[sp++] = i - 1; }
        if (x < w - 1 && clip[i + 1] && !lab[i + 1]) { lab[i + 1] = 1; stack[sp++] = i + 1; }
        if (y > 0 && clip[i - w] && !lab[i - w]) { lab[i - w] = 1; stack[sp++] = i - w; }
        if (y < h - 1 && clip[i + w] && !lab[i + w]) { lab[i + w] = 1; stack[sp++] = i + w; }
      }
      if (area >= GLARE_MIN_BLOB) blobs.push(area, sY / area, sx / area, sy / area, x0, y0, x1, y1);
      if (blobs.length >= MAX_BLOBS * 8) break;
    }
    const region = (bx1 - bx0) * (by1 - by0);
    let gArea = 0, gMax = 0;
    for (let b = 0; b < blobs.length; b += 8) {
      const area = blobs[b], mY = blobs[b + 1], mx = blobs[b + 2], my = blobs[b + 3];
      const x0 = blobs[b + 4], y0 = blobs[b + 5], x1 = blobs[b + 6], y1 = blobs[b + 7];
      // the 3px band around the blob's bbox: is the blob a local peak?
      const ax0 = Math.max(0, x0 - 3), ay0 = Math.max(0, y0 - 3);
      const ax1 = Math.min(w - 1, x1 + 3), ay1 = Math.min(h - 1, y1 + 3);
      let bn = 0, bY = 0, bFg = 0, bE = 0, bEn = 0;
      for (let y = ay0; y <= ay1; y++) {
        for (let x = ax0; x <= ax1; x++) {
          if (x >= x0 && x <= x1 && y >= y0 && y <= y1) continue;
          const i = y * w + x;
          bn++; bY += grey[i];
          if (fgMask && fg[(y >> 3) * cw + (x >> 3)]) bFg++;
          if (x > 0 && y > 0 && x < w - 1 && y < h - 1) {
            const gx = grey[i + 1] - grey[i - 1], gy = grey[i + w] - grey[i - w];
            bE += ((gx < 0 ? -gx : gx) + (gy < 0 ? -gy : gy)) * 0.5;
            bEn++;
          }
        }
      }
      // a blob at the frame edge is the paper or table beyond the pack
      if (x0 === 0 || y0 === 0 || x1 === w - 1 || y1 === h - 1) continue;
      if (!bn || mY - bY / bn < GLARE_PEAK) continue;
      // on the product: centroid inside the bbox, most of the surround is product, and it is printed
      // (a bare metal or plain surface has a highlight, not glare on print)
      const on = mx >= bx0 && mx < bx1 && my >= by0 && my < by1 && bFg >= 0.5 * bn && bEn > 0 && bE / bEn >= GLARE_PRINT;
      if (!on) continue;
      gArea += area;
      if (area > gMax) gMax = area;
    }
    const g = region > 0 ? (gArea / region) * 1000 : 0;

    return { sharp, g, gMax, f, ext, conf, whiteBg, x: xmask, y: yMean, k, p50, p95, clipped, w, h };
  }

  return { analyze };
}

const clampInt = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number.isFinite(v) ? v : lo)));

/** The compact, integers-only record that travels with the photo. */
export function toQ(m, tap = {}) {
  return {
    v: 1,
    s: clampInt(m.sharp, 0, 1e6),
    g: clampInt(m.g, 0, 1000),
    f: m.f < 0 ? -1 : clampInt(m.f * 100, 0, 100),
    x: clampInt(m.x, 0, 15),
    y: clampInt(m.y, 0, 255),
    k: clampInt(m.k, 0, 100),
    e: null,
    m: null,
    h: tap.h || "",
  };
}

/** The one word shown on the tile, or null. */
export function shotFlag(m, { shotIndex = 0, softBelow = SOFT_BELOW } = {}) {
  if (m.y < 50 && m.p95 < 140) return "dark";
  // Blurry before bright: bright is hidden from merchants, and must never mask
  // the blurry badge main already shows.
  if (m.sharp < softBelow) return "blurry";
  if (m.k > 25 && m.p50 >= 250 && !m.whiteBg) return "bright";
  if (m.conf) {
    if (m.ext < TOO_FAR[shotIndex === 0 ? 0 : 1]) return "too far";
    if (m.x !== 0 && m.f < 0.5) return "cut off";
  }
  if (m.g >= GLARE_BAD_PERMILLE && m.gMax >= GLARE_BAD_BLOB) return "glare";
  return null;
}
